/**
 * Owner-only Client Detail / Profile Editor.
 *
 * Without this, the only way to set a client's keywords, watched FB groups,
 * industry, value prop, etc. was direct D1 SQL — meaning a freshly-paid
 * client would never get any leads because their profile is empty by default.
 *
 * Page layout (top to bottom):
 *   1. Client header (name, email, Stripe / extension status pills)
 *   2. Profile card — text fields (name, industry, value_prop, promotion,
 *      tone) + match_mode radio. Saves on blur with a debounce.
 *   3. Keywords card — positive/negative tabs, add row, list with remove.
 *   4. Watched groups card — paste URL, list with enable/disable + remove.
 *
 * All mutations are scoped to the client_account, validated by the
 * /admin/clients/:id sub-routes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AppShell } from '../components/AppShell';
import { useApi, ApiError, type LeadRow } from '../lib/api';
import { LeadItem } from './Leads';

type ClientDetail = {
  client: {
    id: string;
    name: string;
    contact_email: string;
    plan_tier: string | null;
    stripe_status: string | null;
    stripe_customer_id: string | null;
    stripe_subscription_id: string | null;
    created_at: number;
    auto_sweep_interval_minutes?: number | null;
    sweep_requested_at?: number | null;
    /** Raw JSON blob from the DB. Null when no flags ever set. */
    feature_flags_json?: string | null;
  };
  profiles: ProfileRow[];
  keywords: KeywordRow[];
  watched_groups: WatchedGroupRow[];
  watched_subreddits?: WatchedGroupRow[];
};

type ProfileRow = {
  id: string;
  name: string;
  industry: string | null;
  value_proposition: string | null;
  promotion: string | null;
  tone: string | null;
  match_mode: 'includes' | 'exact' | null;
  created_at: number;
  updated_at: number;
};

type KeywordRow = {
  id: string;
  profile_id: string;
  keyword: string;
  kind: 'positive' | 'negative';
  created_at: number;
};

type WatchedGroupRow = {
  id: string;
  group_id: string;
  group_url: string;
  group_name: string | null;
  enabled: 0 | 1;
  created_at: number;
  last_swept_at: number | null;
};

// JSON-textarea editor for the client's feature_flags blob. v1 — a plain
// textarea with parse-on-save. Once we have real flags in active use this can
// graduate to per-flag toggles. The whole point is to be able to roll a risky
// feature to one client (you, your test account) before the rest.
function FeatureFlagsEditor({
  clientId,
  initialJson,
  onSaved,
}: {
  clientId: string;
  initialJson: string | null;
  onSaved: () => Promise<void>;
}) {
  const { request } = useApi();
  // Pretty-print the JSON for editing; empty/null becomes `{}` so the
  // textarea is never blank.
  const pretty = (() => {
    if (!initialJson) return '{}';
    try {
      const parsed = JSON.parse(initialJson);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return initialJson; // show the raw broken JSON so the user can fix it
    }
  })();
  const [text, setText] = useState(pretty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => setText(pretty), [pretty]);

  async function save() {
    setSaving(true);
    setErr(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text.trim() || '{}');
    } catch (e) {
      setErr('Invalid JSON: ' + (e as Error).message);
      setSaving(false);
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setErr('Must be a JSON object like { "flag_name": true }');
      setSaving(false);
      return;
    }
    // Frontend mirror of the backend whitelist: only string→boolean entries
    // are accepted. Warn the user if we'd drop anything.
    const clean: Record<string, boolean> = {};
    let dropped = 0;
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof k === 'string' && typeof v === 'boolean') clean[k] = v;
      else dropped++;
    }
    if (dropped > 0) {
      setErr(
        `${dropped} entr${dropped === 1 ? 'y' : 'ies'} dropped — flags must be string keys with boolean values.`,
      );
      setSaving(false);
      return;
    }
    try {
      await request(`/admin/clients/${clientId}/feature-flags`, {
        method: 'PATCH',
        body: JSON.stringify({ flags: clean }),
      });
      setSavedAt(Date.now());
      window.setTimeout(() => setSavedAt(null), 2500);
      await onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 8 }}>
      <p className="muted" style={{ marginTop: 0, marginBottom: 8, fontSize: 13 }}>
        Per-client feature flags. Both the web app and the extension read this
        every 60 seconds. Use it to canary risky features to one client before
        rolling out to everyone. Format: <code>{`{ "flag_name": true }`}</code>.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
        style={{
          width: '100%',
          minHeight: 120,
          padding: '10px 12px',
          border: '1px solid var(--border)',
          borderRadius: 6,
          fontSize: 13,
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          background: '#fff',
          color: 'var(--text)',
          resize: 'vertical',
        }}
      />
      <div
        className="actions"
        style={{ justifyContent: 'flex-start', marginTop: 10, gap: 10 }}
      >
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void save()}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save flags'}
        </button>
        {err && (
          <span style={{ fontSize: 13, color: '#b91c1c' }}>{err}</span>
        )}
        {savedAt && !err && (
          <span style={{ fontSize: 13, color: '#15803d' }}>
            Saved ✓ — propagates to the extension on the next 60s sync.
          </span>
        )}
      </div>
    </div>
  );
}

// Event keys mirror lib/webhooks.ts WEBHOOK_EVENTS on the backend.
const WEBHOOK_EVENT_LABELS: Record<string, string> = {
  match_captured: 'New lead captured',
  buyer_intent_classified: 'AI classified buyer intent',
  lead_saved: 'Lead saved (starred)',
  lead_won: 'Lead marked won',
  outreach_sent: 'Outreach sent',
};

type WebhookRow = {
  id: string;
  url: string;
  secret: string | null;
  events: string[];
  enabled: boolean;
  created_at: number;
  last_fired_at: number | null;
  last_status: string | null;
};

// Per-client webhook manager. Lists configured endpoints + an add form.
// The backend fires these cloud-side on lead events; nothing here touches the
// extension.
function WebhooksEditor({ clientId }: { clientId: string }) {
  const { request } = useApi();
  const [hooks, setHooks] = useState<WebhookRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<{
    id: string;
    text: string;
    ok: boolean;
  } | null>(null);

  // Add-form state.
  const [newUrl, setNewUrl] = useState('');
  const [newSecret, setNewSecret] = useState('');
  const [newEvents, setNewEvents] = useState<Set<string>>(
    () => new Set(['match_captured']),
  );
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await request<{ webhooks: WebhookRow[] }>(
        `/admin/clients/${clientId}/webhooks`,
      );
      setHooks(res.webhooks);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [clientId, request]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addWebhook() {
    setAddErr(null);
    if (!/^https:\/\//i.test(newUrl.trim())) {
      setAddErr('URL must start with https://');
      return;
    }
    if (newEvents.size === 0) {
      setAddErr('Pick at least one event to subscribe to.');
      return;
    }
    setAdding(true);
    try {
      await request(`/admin/clients/${clientId}/webhooks`, {
        method: 'POST',
        body: JSON.stringify({
          url: newUrl.trim(),
          secret: newSecret.trim() || undefined,
          events: [...newEvents],
        }),
      });
      setNewUrl('');
      setNewSecret('');
      setNewEvents(new Set(['match_captured']));
      await load();
    } catch (e) {
      setAddErr((e as Error).message);
    } finally {
      setAdding(false);
    }
  }

  async function toggleEnabled(h: WebhookRow) {
    setBusyId(h.id);
    try {
      await request(`/admin/clients/${clientId}/webhooks/${h.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !h.enabled }),
      });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function removeWebhook(h: WebhookRow) {
    if (!window.confirm(`Delete this webhook?\n\n${h.url}`)) return;
    setBusyId(h.id);
    try {
      await request(`/admin/clients/${clientId}/webhooks/${h.id}`, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function testWebhook(h: WebhookRow) {
    setBusyId(h.id);
    setTestMsg(null);
    try {
      const res = await request<{ ok: boolean; status: string }>(
        `/admin/clients/${clientId}/webhooks/${h.id}/test`,
        { method: 'POST' },
      );
      setTestMsg({
        id: h.id,
        text: res.ok ? 'Test delivered ✓' : `Test failed — ${res.status}`,
        ok: res.ok,
      });
      await load();
    } catch (e) {
      setTestMsg({
        id: h.id,
        text: 'Test failed: ' + (e as Error).message,
        ok: false,
      });
    } finally {
      setBusyId(null);
    }
  }

  function toggleNewEvent(ev: string) {
    setNewEvents((prev) => {
      const next = new Set(prev);
      if (next.has(ev)) next.delete(ev);
      else next.add(ev);
      return next;
    });
  }

  return (
    <div style={{ marginTop: 8 }}>
      <p className="muted" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
        GroupPulse POSTs a JSON payload to each enabled endpoint when the
        subscribed events happen. Fired from the cloud — works whether or not
        the client&apos;s browser is open. A shared secret, if set, is sent as
        the <code>X-GroupPulse-Secret</code> header.
      </p>

      {err && (
        <p style={{ color: '#b91c1c', fontSize: 13 }}>{err}</p>
      )}

      {/* Existing webhooks */}
      {hooks === null ? (
        <p className="muted">Loading…</p>
      ) : hooks.length === 0 ? (
        <p className="muted" style={{ fontStyle: 'italic', fontSize: 13 }}>
          No webhooks yet. Add one below.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {hooks.map((h) => (
            <div
              key={h.id}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '12px 14px',
                background: h.enabled ? '#fff' : '#f8fafc',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <code
                  style={{
                    fontSize: 13,
                    flex: 1,
                    minWidth: 200,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {h.url}
                </code>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: h.enabled ? '#15803d' : 'var(--muted)',
                  }}
                >
                  {h.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--muted)',
                  marginTop: 6,
                }}
              >
                {h.events
                  .map((e) => WEBHOOK_EVENT_LABELS[e] ?? e)
                  .join(' · ')}
              </div>
              {h.last_status && (
                <div
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                    color: h.last_status === 'ok' ? '#15803d' : '#b91c1c',
                  }}
                >
                  Last delivery: {h.last_status}
                  {h.last_fired_at
                    ? ` · ${new Date(h.last_fired_at).toLocaleString()}`
                    : ''}
                </div>
              )}
              {testMsg && testMsg.id === h.id && (
                <div
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                    color: testMsg.ok ? '#15803d' : '#b91c1c',
                  }}
                >
                  {testMsg.text}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 12, padding: '5px 10px' }}
                  disabled={busyId === h.id}
                  onClick={() => void testWebhook(h)}
                >
                  Send test
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 12, padding: '5px 10px' }}
                  disabled={busyId === h.id}
                  onClick={() => void toggleEnabled(h)}
                >
                  {h.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 12, padding: '5px 10px', color: '#b91c1c' }}
                  disabled={busyId === h.id}
                  onClick={() => void removeWebhook(h)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 14,
          borderTop: '1px solid var(--border)',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          Add a webhook
        </div>
        <input
          type="url"
          value={newUrl}
          placeholder="https://hooks.zapier.com/…"
          onChange={(e) => setNewUrl(e.target.value)}
          style={webhookInputStyle}
        />
        <input
          type="text"
          value={newSecret}
          placeholder="Shared secret (optional)"
          onChange={(e) => setNewSecret(e.target.value)}
          style={{ ...webhookInputStyle, marginTop: 8 }}
        />
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Object.entries(WEBHOOK_EVENT_LABELS).map(([key, label]) => (
            <label
              key={key}
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
            >
              <input
                type="checkbox"
                checked={newEvents.has(key)}
                onChange={() => toggleNewEvent(key)}
              />
              {label}
            </label>
          ))}
        </div>
        <div
          className="actions"
          style={{ justifyContent: 'flex-start', marginTop: 12, gap: 10 }}
        >
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void addWebhook()}
            disabled={adding || !newUrl.trim()}
          >
            {adding ? 'Adding…' : 'Add webhook'}
          </button>
          {addErr && (
            <span style={{ fontSize: 13, color: '#b91c1c' }}>{addErr}</span>
          )}
        </div>
      </div>
    </div>
  );
}

const webhookInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#fff',
  color: 'var(--text)',
};

function AutoSweepPicker({
  clientId,
  currentMinutes,
  onChanged,
}: {
  clientId: string;
  currentMinutes: number | null;
  onChanged: () => Promise<void>;
}) {
  const { request } = useApi();
  const [value, setValue] = useState<number | null>(currentMinutes);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setValue(currentMinutes);
  }, [currentMinutes]);

  async function save(next: number | null) {
    setSaving(true);
    setErr(null);
    try {
      await request(`/admin/clients/${clientId}/auto-sweep`, {
        method: 'PATCH',
        body: JSON.stringify({ interval_minutes: next }),
      });
      setValue(next);
      setSavedAt(Date.now());
      window.setTimeout(() => setSavedAt(null), 2000);
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const options: { label: string; value: number | null }[] = [
    { label: 'Off (manual only)', value: null },
    { label: 'Every 30 minutes', value: 30 },
    { label: 'Every hour', value: 60 },
    { label: 'Every 2 hours', value: 120 },
    { label: 'Every 3 hours', value: 180 },
    { label: 'Every 6 hours', value: 360 },
    { label: 'Every 12 hours', value: 720 },
    { label: 'Every 24 hours', value: 1440 },
  ];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        marginTop: 10,
        padding: '12px 14px',
        background: '#f8fafc',
        border: '1px solid var(--border)',
        borderRadius: 8,
      }}
    >
      <label
        htmlFor={`auto-sweep-${clientId}`}
        style={{ fontSize: 14, fontWeight: 500 }}
      >
        ⏱ Auto-sweep schedule:
      </label>
      <select
        id={`auto-sweep-${clientId}`}
        value={value === null ? '' : String(value)}
        disabled={saving}
        onChange={(e) => {
          const raw = e.target.value;
          const next = raw === '' ? null : parseInt(raw, 10);
          void save(next);
        }}
        style={{
          padding: '6px 10px',
          border: '1px solid var(--border)',
          borderRadius: 6,
          background: '#fff',
          fontSize: 14,
          fontFamily: 'inherit',
        }}
      >
        {options.map((o) => (
          <option key={String(o.value)} value={o.value === null ? '' : String(o.value)}>
            {o.label}
          </option>
        ))}
      </select>
      {saving && <span className="muted" style={{ fontSize: 13 }}>Saving…</span>}
      {savedAt && (
        <span style={{ color: '#047857', fontSize: 13 }}>Saved ✓</span>
      )}
      {err && (
        <span style={{ color: '#b91c1c', fontSize: 13 }}>{err}</span>
      )}
      <span className="muted" style={{ fontSize: 12, flex: 1, minWidth: 220 }}>
        The extension reprograms its alarm within 60 seconds. Each sweep needs
        the client&apos;s browser open.
      </span>
    </div>
  );
}

function SweepNowButton({
  clientId,
  hasGroups,
}: {
  clientId: string;
  hasGroups: boolean;
}) {
  const { request } = useApi();
  const [busy, setBusy] = useState(false);
  const [requestedAt, setRequestedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setBusy(true);
    setErr(null);
    try {
      const res = await request<{ sweep_requested_at: number }>(
        `/admin/clients/${clientId}/sweep`,
        { method: 'POST' },
      );
      setRequestedAt(res.sweep_requested_at);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        marginTop: 14,
        padding: '12px 14px',
        background: '#f8fafc',
        border: '1px solid var(--border)',
        borderRadius: 8,
      }}
    >
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => void go()}
        disabled={busy || !hasGroups}
        style={{ fontSize: 14, padding: '8px 14px' }}
      >
        {busy ? 'Requesting…' : '⚡ Sweep now'}
      </button>
      <div style={{ flex: 1, minWidth: 220 }}>
        {!hasGroups ? (
          <span className="muted" style={{ fontSize: 13 }}>
            Add at least one watched group above before requesting a sweep.
          </span>
        ) : requestedAt ? (
          <span style={{ fontSize: 13, color: '#047857' }}>
            ✓ Sweep requested at {new Date(requestedAt).toLocaleTimeString()}.
            The next extension poll will pick it up within 60 seconds —
            either this client&apos;s own extension, or any agency-mode
            extension that currently has this client selected. The browser
            picking it up must be open.
          </span>
        ) : (
          <span className="muted" style={{ fontSize: 13 }}>
            Triggers a remote sweep across every enabled group. The
            client&apos;s own extension OR any agency-mode extension with
            this client picked as the active client will see the request on
            its next 60-second poll and run the sweep. Whichever browser
            picks it up must be open.
          </span>
        )}
        {err && (
          <span style={{ color: '#b91c1c', fontSize: 13, marginLeft: 8 }}>
            {err}
          </span>
        )}
      </div>
    </div>
  );
}

export function ClientDetail() {
  const { request } = useApi();
  const { id: clientId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await request<ClientDetail>(`/admin/clients/${clientId}`);
      setData(res);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setError('Client not found');
      } else {
        setError((e as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, request]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <AppShell userRole="owner">
      {/* Sticky impersonation banner — makes it unmissable that the owner is
          looking AT a specific client's view, not their own. Stays visible
          while scrolling so context never gets lost mid-edit. */}
      {data && <ClientContextBar client={data.client} />}

      <main className="page">
        {loading ? (
          <section className="card">
            <p className="muted">Loading…</p>
          </section>
        ) : error ? (
          <section className="card">
            <h1>Couldn&apos;t load client</h1>
            <p className="muted">{error}</p>
            <button className="btn btn-secondary" onClick={() => void reload()}>
              Retry
            </button>
            <button
              className="btn btn-secondary"
              style={{ marginLeft: 8 }}
              onClick={() => navigate('/admin/clients')}
            >
              Back to clients
            </button>
          </section>
        ) : data ? (
          <>
            <ClientHeader
              client={data.client}
              onRenamed={(newName) => {
                setData((d) =>
                  d ? { ...d, client: { ...d.client, name: newName } } : d,
                );
              }}
            />
            {data.profiles.length === 0 ? (
              <section className="card">
                <p className="muted">
                  This client has no profile yet. Run{' '}
                  <code>POST /admin/seed</code> first.
                </p>
              </section>
            ) : (
              <>
                <Collapsible
                  id={`pf-${data.client.id}`}
                  title="Profile"
                  subtitle={
                    data.profiles[0]!.industry ||
                    'Tells the AI who this client serves'
                  }
                  defaultOpen
                >
                  <ProfileEditor
                    clientId={data.client.id}
                    profile={data.profiles[0]!}
                    onSaved={reload}
                  />
                </Collapsible>
                <Collapsible
                  id={`kw-${data.client.id}`}
                  title="Keywords"
                  subtitle={`${data.keywords.filter((k) => k.kind === 'positive').length} positive · ${data.keywords.filter((k) => k.kind === 'negative').length} negative`}
                >
                  <KeywordsEditor
                    clientId={data.client.id}
                    profile={data.profiles[0]!}
                    keywords={data.keywords}
                    onChanged={reload}
                  />
                </Collapsible>
                <Collapsible
                  id={`wg-${data.client.id}`}
                  title="Watched groups"
                  subtitle={`${data.watched_groups.filter((g) => g.enabled === 1).length} of ${data.watched_groups.length} enabled · ${
                    data.client.auto_sweep_interval_minutes
                      ? `auto-sweep every ${
                          data.client.auto_sweep_interval_minutes < 60
                            ? `${data.client.auto_sweep_interval_minutes}m`
                            : `${data.client.auto_sweep_interval_minutes / 60}h`
                        }`
                      : 'auto-sweep off'
                  }`}
                >
                  <WatchedGroupsEditor
                    clientId={data.client.id}
                    groups={data.watched_groups}
                    onChanged={reload}
                    platform="facebook"
                  />
                  <AutoSweepPicker
                    clientId={data.client.id}
                    currentMinutes={
                      data.client.auto_sweep_interval_minutes ?? null
                    }
                    onChanged={reload}
                  />
                </Collapsible>
                <Collapsible
                  id={`wr-${data.client.id}`}
                  title="Watched subreddits"
                  subtitle={(() => {
                    const subs = data.watched_subreddits || [];
                    const enabled = subs.filter((g) => g.enabled === 1).length;
                    if (subs.length === 0) return 'none yet — Reddit channel (Premium)';
                    return `${enabled} of ${subs.length} enabled`;
                  })()}
                >
                  <WatchedGroupsEditor
                    clientId={data.client.id}
                    groups={data.watched_subreddits || []}
                    onChanged={reload}
                    platform="reddit"
                  />
                </Collapsible>
                <Collapsible
                  id={`leads-${data.client.id}`}
                  title="Leads"
                  subtitle="Same dashboard the client sees"
                  defaultOpen
                >
                  <ClientLeads clientId={data.client.id} />
                </Collapsible>
                <Collapsible
                  id={`feature-flags-${data.client.id}`}
                  title="Feature flags"
                  subtitle="Per-client toggles for canarying risky changes"
                >
                  <FeatureFlagsEditor
                    clientId={data.client.id}
                    initialJson={data.client.feature_flags_json ?? null}
                    onSaved={reload}
                  />
                </Collapsible>
                <Collapsible
                  id={`webhooks-${data.client.id}`}
                  title="Webhooks"
                  subtitle="Push this client's lead events to a CRM, Zapier, Slack…"
                >
                  <WebhooksEditor clientId={data.client.id} />
                </Collapsible>
                <DangerZone client={data.client} />
              </>
            )}
          </>
        ) : null}
      </main>
    </AppShell>
  );
}

// -- Sub-sections -------------------------------------------------------------

// Collapsible accordion-style section. Each section's open/closed state is
// persisted in localStorage under its `id` so the owner can leave Watched
// Groups closed on a screen-cluttered client and have it stay closed across
// page reloads.
function Collapsible({
  id,
  title,
  subtitle,
  defaultOpen = false,
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const storageKey = `gp.collapsible.${id}`;
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return defaultOpen;
    const v = window.localStorage.getItem(storageKey);
    if (v === '1') return true;
    if (v === '0') return false;
    return defaultOpen;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, open ? '1' : '0');
    }
  }, [open, storageKey]);

  return (
    <section className={`collapsible ${open ? 'is-open' : 'is-closed'}`}>
      <button
        type="button"
        className="collapsible-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div className="collapsible-title-row">
          <h2 className="collapsible-title">{title}</h2>
          {subtitle && <span className="collapsible-subtitle">{subtitle}</span>}
        </div>
        <span className="collapsible-chevron" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && <div className="collapsible-body">{children}</div>}
    </section>
  );
}

// Sticky bar that pins to the top of the viewport while the owner is inside a
// client's view. Visually distinct from the regular dashboard chrome so it's
// obvious you're "impersonating" a client and not editing your own settings.
function ClientContextBar({ client }: { client: ClientDetail['client'] }) {
  const stripeOk =
    client.stripe_status === 'active' || client.stripe_status === 'trialing';
  return (
    <div className="client-context-bar">
      <div className="client-context-bar-inner">
        <Link to="/admin/clients" className="client-context-back">
          ←
        </Link>
        <div className="client-context-eye" aria-hidden="true">
          👁
        </div>
        <div className="client-context-meta">
          <div className="client-context-label">Viewing client</div>
          <div className="client-context-name">
            {client.name}
            <span className="client-context-email">· {client.contact_email}</span>
          </div>
        </div>
        <div className="client-context-pills">
          {client.plan_tier && (
            <span className={`client-context-pill pill-${client.plan_tier}`}>
              {client.plan_tier}
            </span>
          )}
          {client.stripe_status && (
            <span
              className={`client-context-pill ${
                stripeOk ? 'pill-ok' : 'pill-warn'
              }`}
            >
              {client.stripe_status}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientHeader({
  client,
  onRenamed,
}: {
  client: ClientDetail['client'];
  onRenamed: (newName: string) => void;
}) {
  const { request } = useApi();
  const created = new Date(client.created_at).toLocaleDateString();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(client.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the local draft in sync if the cloud value changes underneath us
  // (e.g. someone else renamed it in another tab) and we are not editing.
  useEffect(() => {
    if (!editing) setDraft(client.name);
  }, [client.name, editing]);

  async function save() {
    const trimmed = draft.trim();
    if (!trimmed) {
      setError('Name cannot be empty.');
      return;
    }
    if (trimmed === client.name) {
      setEditing(false);
      setError(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await request<{ id: string; name: string }>(
        `/admin/clients/${client.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ name: trimmed }),
          headers: { 'Content-Type': 'application/json' },
        },
      );
      onRenamed(res.name);
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(client.name);
    setEditing(false);
    setError(null);
  }

  return (
    <section className="card">
      {editing ? (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          <input
            type="text"
            value={draft}
            autoFocus
            disabled={saving}
            maxLength={80}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              else if (e.key === 'Escape') cancel();
            }}
            style={{
              fontSize: 28,
              fontWeight: 700,
              padding: '4px 10px',
              border: '2px solid var(--primary)',
              borderRadius: 8,
              flex: '1 1 280px',
              maxWidth: 480,
            }}
            aria-label="Client name"
          />
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={saving || !draft.trim()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={cancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'baseline',
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          <h1 style={{ margin: 0 }}>{client.name}</h1>
          <button
            className="btn btn-link"
            onClick={() => {
              setDraft(client.name);
              setEditing(true);
            }}
            style={{
              padding: '2px 8px',
              fontSize: 13,
              color: 'var(--primary)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
            title="Rename client — shows in the agency dropdown, the clients list, and everywhere else"
          >
            ✎ Rename
          </button>
        </div>
      )}
      {error && (
        <p style={{ color: '#b91c1c', fontSize: 13, margin: '4px 0 8px' }}>
          {error}
        </p>
      )}
      <p className="muted" style={{ marginTop: 0 }}>
        {client.contact_email} · joined {created}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {client.plan_tier && <Pill>{client.plan_tier}</Pill>}
        {client.stripe_status && <Pill>{client.stripe_status}</Pill>}
        {client.stripe_subscription_id && (
          <Pill mono>{client.stripe_subscription_id}</Pill>
        )}
      </div>
    </section>
  );
}

function ProfileEditor({
  clientId,
  profile,
  onSaved,
}: {
  clientId: string;
  profile: ProfileRow;
  onSaved: () => Promise<void>;
}) {
  const { request } = useApi();
  const [draft, setDraft] = useState({
    name: profile.name ?? '',
    industry: profile.industry ?? '',
    value_proposition: profile.value_proposition ?? '',
    promotion: profile.promotion ?? '',
    tone: profile.tone ?? '',
    match_mode: (profile.match_mode ?? 'includes') as 'includes' | 'exact',
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // -- AI value-prop generation
  const [vpLoading, setVpLoading] = useState(false);
  const [vpErr, setVpErr] = useState<string | null>(null);
  const [vpSuggestions, setVpSuggestions] = useState<string[] | null>(null);

  async function generateValueProp() {
    setVpLoading(true);
    setVpErr(null);
    setVpSuggestions(null);
    try {
      const res = await request<{ suggestions: string[] }>(
        '/admin/ai/suggest-value-prop',
        {
          method: 'POST',
          body: JSON.stringify({
            industry: draft.industry || undefined,
            promotion: draft.promotion || undefined,
            brief_note: draft.value_proposition || undefined,
          }),
        },
      );
      setVpSuggestions(res.suggestions);
    } catch (e) {
      setVpErr((e as Error).message);
    } finally {
      setVpLoading(false);
    }
  }

  const isDirty = useMemo(() => {
    return (
      draft.name !== (profile.name ?? '') ||
      draft.industry !== (profile.industry ?? '') ||
      draft.value_proposition !== (profile.value_proposition ?? '') ||
      draft.promotion !== (profile.promotion ?? '') ||
      draft.tone !== (profile.tone ?? '') ||
      draft.match_mode !== (profile.match_mode ?? 'includes')
    );
  }, [draft, profile]);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await request(`/admin/clients/${clientId}/profile/${profile.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...draft,
          industry: draft.industry || null,
          value_proposition: draft.value_proposition || null,
          promotion: draft.promotion || null,
          tone: draft.tone || null,
        }),
      });
      setSavedAt(Date.now());
      window.setTimeout(() => setSavedAt(null), 2000);
      await onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card">
      <h2 style={{ marginTop: 0 }}>Profile</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Tells GroupPulse who this client serves and how to write replies.
        Used by the AI on every lead score.
      </p>

      <Field label="Profile name">
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          style={inputStyle}
        />
      </Field>
      <Field label="Industry / niche">
        <input
          type="text"
          placeholder="e.g. real estate, fitness coaching, SaaS"
          value={draft.industry}
          onChange={(e) => setDraft((d) => ({ ...d, industry: e.target.value }))}
          style={inputStyle}
        />
      </Field>
      <Field label="Value proposition">
        <textarea
          placeholder="What you do for the people in this audience."
          value={draft.value_proposition}
          onChange={(e) =>
            setDraft((d) => ({ ...d, value_proposition: e.target.value }))
          }
          style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
        />
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => void generateValueProp()}
            disabled={vpLoading || (!draft.industry && !draft.promotion && !draft.value_proposition)}
            style={{
              fontSize: 12,
              padding: '4px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              background: '#fff',
              cursor: 'pointer',
              color: 'var(--text)',
            }}
          >
            {vpLoading ? '…generating' : '✨ Suggest with AI'}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>
            Uses industry + promotion (or current draft as a brief).
          </span>
        </div>
        {vpErr && (
          <p style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>{vpErr}</p>
        )}
        {vpSuggestions && vpSuggestions.length > 0 && (
          <div
            style={{
              marginTop: 8,
              padding: 10,
              background: '#f8fafc',
              border: '1px solid var(--border)',
              borderRadius: 6,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--muted)',
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              Pick one to insert
            </div>
            {vpSuggestions.map((s, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setDraft((d) => ({ ...d, value_proposition: s }));
                    setVpSuggestions(null);
                  }}
                  style={{
                    fontSize: 12,
                    padding: '4px 8px',
                    border: '1px solid var(--primary)',
                    background: '#fff',
                    color: 'var(--primary)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  Use
                </button>
                <p style={{ margin: 0, fontSize: 13 }}>{s}</p>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setVpSuggestions(null)}
              style={{
                fontSize: 12,
                marginTop: 4,
                background: 'none',
                border: 'none',
                color: 'var(--muted)',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Dismiss
            </button>
          </div>
        )}
      </Field>
      <Field label="What you're promoting">
        <textarea
          placeholder="The specific service, product, or offer."
          value={draft.promotion}
          onChange={(e) => setDraft((d) => ({ ...d, promotion: e.target.value }))}
          style={{ ...inputStyle, minHeight: 48, resize: 'vertical' }}
        />
      </Field>
      <Field label="Reply tone">
        <input
          type="text"
          placeholder="e.g. professional and direct"
          value={draft.tone}
          onChange={(e) => setDraft((d) => ({ ...d, tone: e.target.value }))}
          style={inputStyle}
        />
      </Field>
      <Field label="Keyword match mode">
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input
              type="radio"
              name="match_mode"
              value="includes"
              checked={draft.match_mode === 'includes'}
              onChange={() => setDraft((d) => ({ ...d, match_mode: 'includes' }))}
            />
            <span>Substring</span>
            <span className="muted" style={{ fontSize: 12 }}>
              (broader)
            </span>
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input
              type="radio"
              name="match_mode"
              value="exact"
              checked={draft.match_mode === 'exact'}
              onChange={() => setDraft((d) => ({ ...d, match_mode: 'exact' }))}
            />
            <span>Word boundary</span>
            <span className="muted" style={{ fontSize: 12 }}>
              (cleaner)
            </span>
          </label>
        </div>
      </Field>

      <div className="actions" style={{ justifyContent: 'flex-start' }}>
        <button
          className="btn btn-primary"
          disabled={!isDirty || saving}
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : 'Save profile'}
        </button>
        {savedAt && (
          <span style={{ color: '#10b981', fontSize: 14 }}>Saved ✓</span>
        )}
        {err && <span style={{ color: '#b91c1c', fontSize: 14 }}>{err}</span>}
      </div>
    </section>
  );
}

function KeywordsEditor({
  clientId,
  profile,
  keywords,
  onChanged,
}: {
  clientId: string;
  profile: ProfileRow;
  keywords: KeywordRow[];
  onChanged: () => Promise<void>;
}) {
  const { request } = useApi();
  const [newKw, setNewKw] = useState('');
  const [kind, setKind] = useState<'positive' | 'negative'>('positive');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // -- AI suggestion state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiErr, setAiErr] = useState<string | null>(null);
  const [aiSugg, setAiSugg] = useState<{ positive: string[]; negative: string[] } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  const positive = keywords.filter((k) => k.kind === 'positive');
  const negative = keywords.filter((k) => k.kind === 'negative');

  async function add() {
    if (!newKw.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await request(`/admin/clients/${clientId}/keywords`, {
        method: 'POST',
        body: JSON.stringify({ profile_id: profile.id, keyword: newKw.trim(), kind }),
      });
      setNewKw('');
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(kwId: string) {
    setBusy(true);
    setErr(null);
    try {
      await request(`/admin/clients/${clientId}/keywords/${kwId}`, {
        method: 'DELETE',
      });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function suggestWithAi() {
    setAiLoading(true);
    setAiErr(null);
    setAiSugg(null);
    setSelected(new Set());
    try {
      const res = await request<{ positive: string[]; negative: string[] }>(
        '/admin/ai/suggest-keywords',
        {
          method: 'POST',
          body: JSON.stringify({
            industry: profile.industry ?? undefined,
            value_proposition: profile.value_proposition ?? undefined,
            promotion: profile.promotion ?? undefined,
            existing_keywords: {
              positive: positive.map((k) => k.keyword),
              negative: negative.map((k) => k.keyword),
            },
          }),
        },
      );
      setAiSugg(res);
      // Default-select every suggestion so the owner just hits Add Selected.
      const pre = new Set<string>();
      for (const k of res.positive) pre.add('pos:' + k);
      for (const k of res.negative) pre.add('neg:' + k);
      setSelected(pre);
    } catch (e) {
      setAiErr((e as Error).message);
    } finally {
      setAiLoading(false);
    }
  }

  function toggleSel(key: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }

  async function addSelected() {
    if (!aiSugg) return;
    setAdding(true);
    setErr(null);
    try {
      // Sequential — order matters for UI feedback, and the volume is small.
      for (const k of aiSugg.positive) {
        if (selected.has('pos:' + k)) {
          await request(`/admin/clients/${clientId}/keywords`, {
            method: 'POST',
            body: JSON.stringify({ profile_id: profile.id, keyword: k, kind: 'positive' }),
          });
        }
      }
      for (const k of aiSugg.negative) {
        if (selected.has('neg:' + k)) {
          await request(`/admin/clients/${clientId}/keywords`, {
            method: 'POST',
            body: JSON.stringify({ profile_id: profile.id, keyword: k, kind: 'negative' }),
          });
        }
      }
      setAiSugg(null);
      setSelected(new Set());
      await onChanged();
    } catch (e) {
      // 409 (duplicate) on individual terms is fine — surface the message but keep going.
      setErr((e as Error).message);
    } finally {
      setAdding(false);
    }
  }

  const canSuggest =
    !!(profile.industry || profile.value_proposition || profile.promotion);

  return (
    <section className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ marginTop: 0, marginBottom: 0 }}>Keywords</h2>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => void suggestWithAi()}
          disabled={!canSuggest || aiLoading}
          style={{ fontSize: 13, padding: '6px 12px' }}
        >
          {aiLoading ? '…thinking' : '✨ Suggest with AI'}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 8 }}>
        Positive keywords surface matching posts. Negative keywords filter them
        out. Both are case-insensitive.
      </p>
      {!canSuggest && (
        <p className="muted" style={{ fontSize: 13, fontStyle: 'italic' }}>
          AI suggestions are unlocked once you save an industry, value
          proposition, or promotion above.
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <input
          type="text"
          placeholder="add keyword…"
          value={newKw}
          onChange={(e) => setNewKw(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void add()}
          style={{ ...inputStyle, flex: '1 1 200px', maxWidth: 280 }}
        />
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as 'positive' | 'negative')}
          style={{ ...inputStyle, width: 140 }}
        >
          <option value="positive">Positive</option>
          <option value="negative">Negative</option>
        </select>
        <button
          className="btn btn-primary"
          onClick={() => void add()}
          disabled={busy || !newKw.trim()}
        >
          Add
        </button>
      </div>

      {err && <p style={{ color: '#b91c1c', fontSize: 14 }}>{err}</p>}
      {aiErr && <p style={{ color: '#b91c1c', fontSize: 14 }}>{aiErr}</p>}

      {aiSugg && (
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 14,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              AI suggestions — {selected.size} selected
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  setSelected(
                    selected.size > 0
                      ? new Set()
                      : new Set([
                          ...aiSugg.positive.map((k) => 'pos:' + k),
                          ...aiSugg.negative.map((k) => 'neg:' + k),
                        ]),
                  )
                }
                style={{ fontSize: 12, padding: '4px 10px' }}
              >
                {selected.size > 0 ? 'Clear' : 'Select all'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void addSelected()}
                disabled={adding || selected.size === 0}
                style={{ fontSize: 12, padding: '4px 10px' }}
              >
                {adding ? 'Adding…' : `Add ${selected.size}`}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setAiSugg(null);
                  setSelected(new Set());
                }}
                style={{ fontSize: 12, padding: '4px 10px' }}
              >
                Dismiss
              </button>
            </div>
          </div>
          <SuggestionGroup
            label="Positive"
            color="#10b981"
            items={aiSugg.positive}
            prefix="pos"
            selected={selected}
            toggle={toggleSel}
          />
          <SuggestionGroup
            label="Negative"
            color="#dc2626"
            items={aiSugg.negative}
            prefix="neg"
            selected={selected}
            toggle={toggleSel}
          />
        </div>
      )}

      <KeywordList
        title="Positive"
        items={positive}
        onRemove={(id) => void remove(id)}
        emptyHint="No positive keywords yet. The extension won't capture anything until at least one is set."
        color="#10b981"
      />
      <KeywordList
        title="Negative"
        items={negative}
        onRemove={(id) => void remove(id)}
        emptyHint="No filter words yet."
        color="#dc2626"
      />
    </section>
  );
}

function SuggestionGroup({
  label,
  color,
  items,
  prefix,
  selected,
  toggle,
}: {
  label: string;
  color: string;
  items: string[];
  prefix: string;
  selected: Set<string>;
  toggle: (key: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {label} ({items.length})
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map((k) => {
          const key = `${prefix}:${k}`;
          const on = selected.has(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: on ? color : '#fff',
                color: on ? '#fff' : color,
                border: `1px solid ${color}`,
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 12,
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              {on ? '✓' : '+'} {k}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function KeywordList({
  title,
  items,
  onRemove,
  emptyHint,
  color,
}: {
  title: string;
  items: KeywordRow[];
  onRemove: (id: string) => void;
  emptyHint: string;
  color: string;
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 8,
        }}
      >
        {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, fontStyle: 'italic' }}>
          {emptyHint}
        </p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {items.map((kw) => (
            <span
              key={kw.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#f1f5f9',
                border: `1px solid ${color}33`,
                color,
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 13,
              }}
            >
              {kw.keyword}
              <button
                onClick={() => onRemove(kw.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--muted)',
                  cursor: 'pointer',
                  fontSize: 16,
                  lineHeight: 1,
                  padding: 0,
                }}
                aria-label={`Remove ${kw.keyword}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function WatchedGroupsEditor({
  clientId,
  groups,
  onChanged,
  platform = 'facebook',
}: {
  clientId: string;
  groups: WatchedGroupRow[];
  onChanged: () => Promise<void>;
  platform?: 'facebook' | 'reddit';
}) {
  const { request } = useApi();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const labels =
    platform === 'reddit'
      ? {
          heading: 'Subreddits',
          help:
            'The extension only captures from subreddits in this list. Paste a reddit.com/r/<name> URL — or just /r/<name>.',
          placeholder: 'https://www.reddit.com/r/SaaS',
          empty:
            "No subreddits yet. The Reddit channel won't capture leads until at least one is on the list.",
          nameField: 'Display name (optional)',
        }
      : {
          heading: 'Groups',
          help:
            'The extension only captures from groups in this list. Paste a facebook.com/groups/… URL to add one.',
          placeholder: 'https://www.facebook.com/groups/…',
          empty:
            "No groups yet. The client won't capture leads until at least one group is watched.",
          nameField: 'Group name (optional)',
        };

  async function add() {
    if (!url.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await request(`/admin/clients/${clientId}/watched-groups`, {
        method: 'POST',
        body: JSON.stringify({
          group_url: url.trim(),
          group_name: name.trim() || undefined,
          platform,
        }),
      });
      setUrl('');
      setName('');
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(g: WatchedGroupRow) {
    setBusy(true);
    setErr(null);
    try {
      await request(`/admin/clients/${clientId}/watched-groups/${g.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: g.enabled === 0 }),
      });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(g: WatchedGroupRow) {
    if (!confirm(`Remove ${g.group_name ?? g.group_url} from the watch list?`)) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await request(`/admin/clients/${clientId}/watched-groups/${g.id}`, {
        method: 'DELETE',
      });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const hasEnabledGroups = groups.some((g) => g.enabled === 1);

  return (
    <section className="card">
      <h2 style={{ marginTop: 0 }}>{labels.heading}</h2>
      <p className="muted" style={{ marginTop: 0 }}>{labels.help}</p>

      {platform === 'facebook' && (
        <SweepNowButton clientId={clientId} hasGroups={hasEnabledGroups} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, marginBottom: 16 }}>
        <input
          type="text"
          placeholder={labels.placeholder}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void add()}
          style={inputStyle}
        />
        <input
          type="text"
          placeholder={labels.nameField}
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />
        <button
          className="btn btn-primary"
          onClick={() => void add()}
          disabled={busy || !url.trim()}
        >
          Add
        </button>
      </div>
      {err && <p style={{ color: '#b91c1c', fontSize: 14 }}>{err}</p>}

      {groups.length === 0 ? (
        <p className="muted" style={{ fontStyle: 'italic' }}>{labels.empty}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {groups.map((g) => (
            <li
              key={g.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
                borderTop: '1px solid var(--border)',
              }}
            >
              <label
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <input
                  type="checkbox"
                  checked={g.enabled === 1}
                  onChange={() => void toggle(g)}
                  disabled={busy}
                />
                <span className="muted" style={{ fontSize: 12 }}>
                  {g.enabled === 1 ? 'on' : 'off'}
                </span>
              </label>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>
                  {g.group_name ?? g.group_id}
                </div>
                <a
                  href={g.group_url}
                  target="_blank"
                  rel="noreferrer"
                  className="muted"
                  style={{
                    fontSize: 12,
                    wordBreak: 'break-all',
                    textDecoration: 'none',
                  }}
                >
                  {g.group_url}
                </a>
              </div>
              <button
                className="btn btn-secondary"
                style={{ fontSize: 13, padding: '6px 10px' }}
                onClick={() => void remove(g)}
                disabled={busy}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// -- Bits ---------------------------------------------------------------------

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Pill({
  children,
  mono = false,
}: {
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <span
      style={{
        background: '#f1f5f9',
        color: 'var(--text)',
        fontSize: 12,
        padding: '4px 10px',
        borderRadius: 999,
        fontFamily: mono
          ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
          : 'inherit',
      }}
    >
      {children}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  background: '#fff',
  color: 'var(--text)',
};

// -----------------------------------------------------------------------------
// ClientLeads — same dashboard the client sees, scoped to this client_account.
// Owner edits made here (status / notes / outreached) sync back to the
// extension via the existing 60s /api/leads/updates round-trip. Inversely,
// anything the extension uploads next sync appears here.
// -----------------------------------------------------------------------------

type LeadsPayload = {
  counts: { total: number; buyer_intent: number; new_leads: number; won_leads: number };
  leads: LeadRow[];
  next_cursor: number | null;
  pagination: {
    page: number;
    page_size: number;
    total_pages: number;
    total: number;
  };
};

const PAGE_SIZE = 20;

function ClientLeads({ clientId }: { clientId: string }) {
  const { request } = useApi();
  const [data, setData] = useState<LeadsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const fetchPage = useCallback(
    async (pageNum: number) => {
      const qs = new URLSearchParams({
        limit: String(PAGE_SIZE),
        page: String(pageNum),
      });
      return request<LeadsPayload>(`/admin/clients/${clientId}/leads?${qs}`);
    },
    [clientId, request],
  );

  useEffect(() => {
    setLoading(true);
    setErr(null);
    fetchPage(page)
      .then((res) => setData(res))
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, [fetchPage, page]);

  function onLeadUpdated(updated: LeadRow) {
    setData((d) =>
      d
        ? {
            ...d,
            leads: d.leads.map((l) => (l.id === updated.id ? updated : l)),
          }
        : d,
    );
  }

  // Per-row archive from the agency view. Hits the same /api/me/leads/archive
  // endpoint the client dashboard uses — owner role is allowed via leadScopeClause.
  // On success we just drop the row from the loaded page (this view defaults
  // to active leads, so anything archived leaves the list).
  async function archiveLead(id: string, archived: boolean) {
    try {
      await request('/api/me/leads/archive', {
        method: 'POST',
        body: JSON.stringify({ ids: [id], archived }),
      });
      setData((d) =>
        d
          ? { ...d, leads: d.leads.filter((l) => l.id !== id) }
          : d,
      );
    } catch (e) {
      console.error('[ClientDetail] archive failed:', e);
      // Surface the failure so the user doesn't think the click silently
      // worked — the row would otherwise stay on screen with no signal.
      window.alert(
        `Couldn't ${archived ? 'archive' : 'restore'} lead: ${(e as Error).message}`,
      );
    }
  }

  function goToPage(p: number) {
    if (!data) return;
    const max = data.pagination.total_pages;
    const next = Math.max(1, Math.min(p, max));
    if (next !== page) {
      setPage(next);
      // Scroll the leads section back to its top on page change.
      window.requestAnimationFrame(() => {
        document
          .getElementById('client-leads-section')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  return (
    <section className="card" id="client-leads-section">
      <h2 style={{ marginTop: 0, marginBottom: 4 }}>Leads</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Exactly what this client sees on their own dashboard. Any edit you
        make here syncs back to the extension within 60 seconds.
      </p>

      {data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 12,
            margin: '16px 0',
          }}
        >
          <StatCard label="Total leads" value={data.counts.total} />
          <StatCard label="Buyer intent" value={data.counts.buyer_intent} />
          <StatCard label="New" value={data.counts.new_leads} />
          <StatCard label="Won" value={data.counts.won_leads} />
        </div>
      )}

      {loading ? (
        <p className="muted">Loading leads…</p>
      ) : err ? (
        <p style={{ color: '#b91c1c' }}>{err}</p>
      ) : !data || data.leads.length === 0 ? (
        <p className="muted" style={{ fontStyle: 'italic' }}>
          No leads captured yet. As soon as the extension uploads matches for
          this client they&apos;ll appear here.
        </p>
      ) : (
        <>
          <PaginationSummary pagination={data.pagination} />
          <ul className="lead-list">
            {data.leads.map((lead) => (
              <LeadItem
                key={lead.id}
                lead={lead}
                onUpdated={onLeadUpdated}
                onArchive={(id, archived) => void archiveLead(id, archived)}
              />
            ))}
          </ul>
          {data.pagination.total_pages > 1 && (
            <Pager
              page={data.pagination.page}
              totalPages={data.pagination.total_pages}
              onChange={goToPage}
            />
          )}
        </>
      )}
    </section>
  );
}

// One-line summary above the list: "Showing 1–20 of 84 leads"
function PaginationSummary({
  pagination,
}: {
  pagination: LeadsPayload['pagination'];
}) {
  const from = (pagination.page - 1) * pagination.page_size + 1;
  const to = Math.min(pagination.page * pagination.page_size, pagination.total);
  if (pagination.total === 0) return null;
  return (
    <p
      className="muted"
      style={{ fontSize: 13, margin: '0 0 12px', textAlign: 'right' }}
    >
      Showing <strong>{from.toLocaleString()}</strong>–
      <strong>{to.toLocaleString()}</strong> of{' '}
      <strong>{pagination.total.toLocaleString()}</strong> leads
    </p>
  );
}

// Page-number control. Always shows first, last, current ± 1, with ellipses
// for the gaps. Mirrors the GitHub / Stripe dashboard pattern.
function Pager({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  const pages = buildPageList(page, totalPages);
  return (
    <nav className="pager" aria-label="Lead pagination">
      <button
        type="button"
        className="pager-btn"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
      >
        ← Prev
      </button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className="pager-gap">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className={`pager-btn ${p === page ? 'is-current' : ''}`}
            onClick={() => onChange(p)}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        ),
      )}
      <button
        type="button"
        className="pager-btn"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
      >
        Next →
      </button>
    </nav>
  );
}

// Builds a compact list of page numbers with `…` gaps. Examples (current=*)
//   1 pages total       → [1*]
//   5  / current=3      → [1, 2, 3*, 4, 5]
//   12 / current=1      → [1*, 2, …, 12]
//   12 / current=7      → [1, …, 6, 7*, 8, …, 12]
//   12 / current=12     → [1, …, 11, 12*]
function buildPageList(current: number, total: number): (number | '…')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out: (number | '…')[] = [1];
  if (current > 3) out.push('…');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let p = start; p <= end; p++) out.push(p);
  if (current < total - 2) out.push('…');
  out.push(total);
  return out;
}

// -----------------------------------------------------------------------------
// Danger zone — archive (reversible) + hard-delete (irreversible) controls.
// Lives at the bottom of /admin/clients/:id, collapsed by default.
// -----------------------------------------------------------------------------
function DangerZone({ client }: { client: ClientDetail['client'] }) {
  const { request } = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // -- archive
  const [archiving, setArchiving] = useState(false);
  const [archiveMsg, setArchiveMsg] = useState<string | null>(null);
  const [archiveErr, setArchiveErr] = useState<string | null>(null);

  // -- hard delete
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const confirmTarget = client.contact_email;
  const confirmMatches = confirmText.trim().toLowerCase() === confirmTarget.toLowerCase();

  async function archiveClient() {
    if (
      !confirm(
        `Cancel ${client.name}'s subscription and archive their account? Their data stays in the database for 30 days in case you change your mind.`,
      )
    ) {
      return;
    }
    setArchiving(true);
    setArchiveErr(null);
    setArchiveMsg(null);
    try {
      const res = await request<{
        archived: boolean;
        stripe: { cancelled: boolean; error?: string };
      }>(`/admin/clients/${client.id}/archive`, { method: 'POST' });
      if (res.stripe.cancelled) {
        setArchiveMsg('Subscription cancelled in Stripe and account archived.');
      } else if (res.stripe.error) {
        setArchiveMsg(
          'Account archived. Stripe cancellation note: ' + res.stripe.error,
        );
      } else {
        setArchiveMsg('Account archived (no active Stripe subscription).');
      }
      // Don't navigate away — the owner may want to verify the changes.
    } catch (e) {
      setArchiveErr((e as Error).message);
    } finally {
      setArchiving(false);
    }
  }

  async function hardDeleteClient() {
    if (!confirmMatches) return;
    setDeleting(true);
    setDeleteErr(null);
    try {
      await request(`/admin/clients/${client.id}`, { method: 'DELETE' });
      navigate('/admin/clients');
    } catch (e) {
      setDeleteErr((e as Error).message);
      setDeleting(false);
    }
  }

  return (
    <section className="danger-zone">
      <button
        type="button"
        className="danger-zone-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div>
          <h2 className="danger-zone-title">⚠ Danger zone</h2>
          <p className="danger-zone-sub">
            Archive or permanently delete this client.
          </p>
        </div>
        <span className="collapsible-chevron" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && (
        <div className="danger-zone-body">
          {/* Archive */}
          <div className="danger-row">
            <div className="danger-row-text">
              <div className="danger-row-title">Cancel & archive subscription</div>
              <p className="danger-row-desc">
                Cancels their Stripe subscription, marks them as cancelled, and
                schedules deletion in 30 days. Reversible if they reactivate in
                Stripe within the grace window. Lead history stays intact.
              </p>
              {archiveMsg && (
                <p style={{ color: '#047857', fontSize: 13, margin: '6px 0 0' }}>
                  ✓ {archiveMsg}
                </p>
              )}
              {archiveErr && (
                <p style={{ color: '#b91c1c', fontSize: 13, margin: '6px 0 0' }}>
                  {archiveErr}
                </p>
              )}
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => void archiveClient()}
              disabled={archiving}
            >
              {archiving ? 'Archiving…' : 'Archive'}
            </button>
          </div>

          <hr className="danger-divider" />

          {/* Hard delete */}
          <div className="danger-row danger-row-destructive">
            <div className="danger-row-text">
              <div className="danger-row-title">Delete permanently</div>
              <p className="danger-row-desc">
                Irreversibly removes the client account, their profile, all
                captured leads, keywords, watched groups, and extension tokens.
                Also cancels their active Stripe subscription. Audit log entries
                are preserved with a null reference. <strong>Cannot be undone.</strong>
              </p>
              <label
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 500,
                  marginTop: 10,
                }}
              >
                Type <code>{confirmTarget}</code> to confirm
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={confirmTarget}
                style={{
                  width: '100%',
                  maxWidth: 360,
                  padding: '8px 10px',
                  marginTop: 6,
                  border: '1px solid #fca5a5',
                  borderRadius: 6,
                  fontSize: 14,
                  fontFamily: 'ui-monospace, monospace',
                  background: '#fff',
                }}
              />
              {deleteErr && (
                <p style={{ color: '#b91c1c', fontSize: 13, margin: '6px 0 0' }}>
                  {deleteErr}
                </p>
              )}
            </div>
            <button
              className="btn-danger"
              onClick={() => void hardDeleteClient()}
              disabled={!confirmMatches || deleting}
            >
              {deleting ? 'Deleting…' : 'Delete forever'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div
      style={{
        background: '#f8fafc',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--muted)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700 }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}
